import axios from 'axios';

import config from "../apiServices/appConfig";
const { BASE_URL ,API_VERSION} = config;


export interface DocumentUploadRequest {
  file: File;
  documentType: string;
  businessId?: string;
  userId?: string;
}

export interface DocumentUploadResponse {
  success: boolean;
  documentId: string;
  fileName: string;
  documentType: string;
  uploadDate: string;
  status: 'pending' | 'verified' | 'rejected';
  message?: string;
}

export interface KYBDocument {
  id: string;
  fileName: string;
  documentType: string;
  uploadDate: string;
  status: 'pending' | 'verified' | 'rejected';
  fileUrl?: string;
  remarks?: string;
}

export interface KYBStatus {
  businessId: string;
  status: 'pending' | 'in_review' | 'approved' | 'rejected';
  documents: KYBDocument[];
  submittedDate: string;
  reviewDate?: string;
  remarks?: string;
}

class KYBService {
  private baseURL = process.env.REACT_APP_API_URL || `${BASE_URL}${API_VERSION}`;

  /**
   * Upload a document for KYB verification
   */
  async uploadDocument(request: DocumentUploadRequest): Promise<DocumentUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', request.file);
      formData.append('document_type', request.documentType);
      if (request.businessId) {
        formData.append('businessId', request.businessId);
      }
      if (request.userId) {
        formData.append('userId', request.userId);
      }

      // Ensure access_token is sent in the Authorization header as Bearer
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await axios.post<DocumentUploadResponse>(
        `${this.baseURL}/kyb/upload-document`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Document upload failed');
      }
      throw new Error('Document upload failed');
    }
  }

  /**
   * Get KYB status for a business
   */
  async getKYBStatus(businessId: string): Promise<KYBStatus> {
    try {
      const response = await axios.get<KYBStatus>(
        `${this.baseURL}/kyb/status/${businessId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch KYB status');
      }
      throw new Error('Failed to fetch KYB status');
    }
  }

  /**
   * Submit KYB application
   */
  async submitKYBApplication(businessData: any): Promise<{ businessId: string; status: string }> {
    try {

      // Ensure access_token is sent in the Authorization header as Bearer
      const accessToken = localStorage.getItem('access_token');
      console.log('Access Token:', accessToken); // Debugging line to check access token
      if (!accessToken) {
        throw new Error('No access token found');
      }
      const response = await axios.post<{ businessId: string; status: string }>(
        `${this.baseURL}/kyb/submit`,
        businessData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to submit KYB application');
      }
      throw new Error('Failed to submit KYB application');
    }
  }

  /**
   * Delete a document
   */
  async deleteDocument(documentId: string): Promise<void> {
    try {
      await axios.delete(`${this.baseURL}/kyb/document/${documentId}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to delete document');
      }
      throw new Error('Failed to delete document');
    }
  }

  /**
   * Get document types based on business type
   */
  getRequiredDocuments(businessType: string): string[] {
    const documentTypes: { [key: string]: string[] } = {
      'Private Limited Company': [
        'moaAoa',
        'certificateOfIncorporation',
        'pan_entity',
        'gstRegistrationCertificate',
        'listOfShareholders',
        'listOfDirectors',
        'kycDirectors',
        'kycUbos',
        'bankStatementOneYear',
        'itrTwoYears',
        'declarationNatureOfBusiness',
        'declarationAuthorisedSignatories',
        'declarationSourceOfWealth'
      ],
      'Public Limited Company': [
        'moaAoa',
        'certificateOfIncorporation',
        'pan_entity',
        'gstRegistrationCertificate',
        'listOfShareholders',
        'listOfDirectors',
        'kycDirectors',
        'kycUbos',
        'bankStatementOneYear',
        'itrTwoYears',
        'declarationNatureOfBusiness',
        'declarationAuthorisedSignatories',
        'declarationSourceOfWealth'
      ],
      'LLC': [
        'moaAoa',
        'certificateOfIncorporation',
        'pan_entity',
        'gstRegistrationCertificate',
        'listOfShareholders',
        'listOfDirectors',
        'kycDirectors',
        'kycUbos',
        'bankStatementOneYear',
        'itrTwoYears',
        'declarationNatureOfBusiness',
        'declarationAuthorisedSignatories',
        'declarationSourceOfWealth'
      ],
      'Partnership': [
        'partnershipDeed',
        'msmeUdyamAayogCertificate',
        'gstCertificate',
        'registrationCertificate',
        'itrTwoFinancialYears',
        'panOfFirm',
        'bankStatementOneYearPartnership',
        'operationalOfficeAddressProof',
        'kycAllPartners',
        'declarationsAndUndertakings'
      ],
      'Sole Proprietorship': [
        'pan_entity',
        'gstRegistrationCertificate',
        'bankStatementOneYear',
        'itrTwoYears',
        'operationalOfficeAddressProof',
        'declarationNatureOfBusiness'
      ]
    };

    return documentTypes[businessType] || [];
  }

  /**
   * Get document display names
   */
  getDocumentDisplayName(documentType: string): string {
    const displayNames: { [key: string]: string } = {
      'moaAoa': 'MoA & AoA of the entity',
      'certificateOfIncorporation': 'Certificate of Incorporation',
      'pan_entity': 'PAN of the entity',
      'gstRegistrationCertificate': 'GST Registration Certificate',
      'listOfShareholders': 'List of Shareholders',
      'listOfDirectors': 'List of Directors',
      'kycDirectors': 'KYC of all directors (PAN, Aadhar/Voter-ID/Passport and Utility Bill)',
      'kycUbos': 'KYC of all UBO\'s (those having 10% or more in shareholding Pattern)',
      'bankStatementOneYear': 'Bank statements of 1 year',
      'itrTwoYears': 'ITR of 2 years',
      'declarationNatureOfBusiness': 'Declaration of Nature of Business (on company\'s letterhead, duly signed and sealed)',
      'declarationAuthorisedSignatories': 'Declaration of Authorised Signatories',
      'declarationSourceOfWealth': 'Declaration of Source of wealth',
      'partnershipDeed': 'Partnership deed',
      'msmeUdyamAayogCertificate': 'MSME udyam aayog certificate (if applicable)',
      'gstCertificate': 'GST certificate',
      'registrationCertificate': 'Registration certificate',
      'itrTwoFinancialYears': 'ITR for 2 financial years',
      'panOfFirm': 'PAN of firm',
      'bankStatementOneYearPartnership': 'Bank statement for 1 year',
      'operationalOfficeAddressProof': 'Operational office address proof (lease deed)',
      'kycAllPartners': 'KYC of all partners',
      'declarationsAndUndertakings': 'Declarations and undertakings'
    };

    return displayNames[documentType] || documentType;
  }
}

export const kybService = new KYBService();
export default kybService; 